import { useState } from "react";
import { PiggyBank, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCollection, usePrefs } from "@/hooks/useCollection";
import { rupiah, sum, todayIso, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { EntryForm, ItemList, Section } from "./shared";

const EmergencyCard = ({ total, prefs, save }) => {
  const [target, setTarget] = useState("");
  const t = prefs?.emergency_target || 0;
  const pct = t ? Math.min((total / t) * 100, 100) : 0;
  return (
    <Section title="Dana Daruratku" className="bg-sky-50/70">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-8 h-8 text-sky-400" />
        <div>
          <p className="font-heading text-2xl font-semibold text-sky-600" data-testid="emergency-total">{rupiah(total)}</p>
          <p className="text-xs text-gray-500">Target: <span data-testid="emergency-target">{t ? rupiah(t) : "belum diatur"}</span></p>
        </div>
      </div>
      <div className="h-3 bg-white rounded-full mt-4 overflow-hidden"><div className="h-full bg-sky-400 rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <p className="text-xs text-gray-500 mt-1">{pct.toFixed(0)}% tercapai · disarankan 3-6x pengeluaran bulanan</p>
      <div className="flex gap-2 mt-4">
        <input className="field" type="number" placeholder="Atur target (Rp)" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="emergency-target-input" />
        <Button className="h-11 rounded-2xl" variant="secondary" data-testid="emergency-target-save" onClick={async () => { await save({ emergency_target: Number(target) }); setTarget(""); toast.success("Target disimpan"); }} disabled={!target}>Simpan</Button>
      </div>
    </Section>
  );
};

export default function SavingsTab() {
  const { items, add, remove } = useCollection("savings");
  const { prefs, save } = usePrefs();
  const savings = items.filter((x) => x.kind === "savings");
  const emergency = items.filter((x) => x.kind === "emergency");

  return (
    <div className="space-y-4" data-testid="duitku-savings">
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Tabunganku" className="bg-pink-50/70">
          <div className="flex items-center gap-3">
            <PiggyBank className="w-8 h-8 text-pink-400" />
            <p className="font-heading text-3xl font-semibold text-pink-500" data-testid="savings-total">{rupiah(sum(savings))}</p>
          </div>
          <p className="text-xs text-gray-500 mt-2">{savings.length} catatan setoran/penarikan</p>
        </Section>
        <EmergencyCard total={sum(emergency)} prefs={prefs} save={save} />
      </div>
      <Section title="Setor atau tarik">
        <EntryForm
          testid="savings-form"
          initial={{ kind: "savings", dir: "in", amount: "", date: todayIso(), note: "" }}
          fields={[
            { name: "kind", label: "Ke", type: "select", options: [["savings", "Tabunganku"], ["emergency", "Dana Daruratku"]], keep: true },
            { name: "dir", label: "Aksi", type: "select", options: [["in", "Setor"], ["out", "Tarik"]] },
            { name: "amount", label: "Jumlah (Rp)", type: "number", required: true, min: 1 },
            { name: "date", label: "Tanggal", type: "date", required: true, keep: true },
          ]}
          onSubmit={(v) => add({ kind: v.kind, amount: Number(v.amount) * (v.dir === "out" ? -1 : 1), date: v.date, note: v.note || "" })}
        />
        <div className="mt-4">
          <ItemList
            testid="savings-list"
            items={items}
            onDelete={remove}
            empty="Belum ada setoran. Sedikit demi sedikit lama-lama jadi bukit!"
            render={(it) => ({
              title: it.kind === "savings" ? "Tabunganku" : "Dana Daruratku",
              subtitle: fmtDate(it.date),
              right: <span className={`text-sm font-semibold whitespace-nowrap ${it.amount < 0 ? "text-rose-500" : "text-emerald-500"}`}>{it.amount < 0 ? "-" : "+"}{rupiah(Math.abs(it.amount))}</span>,
            })}
          />
        </div>
      </Section>
    </div>
  );
}
